import { useState } from "react";
import { P, pub } from "@/lib/publicApi";
import { toast } from "sonner";
import { Gavel, ShieldCheck, Upload, Loader2, CheckCircle2, FileText, X } from "lucide-react";

const field = "w-full px-3 py-2.5 text-sm rounded-sm border outline-none bg-white text-[#12263A] placeholder:text-[#94A3B8] focus:border-[#12263A]";
const ACCEPT = "image/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.mp3,.wav,.m4a,.aac,.ogg,.flac";
const DOC_TYPES = ["Civil process", "Return of service", "Writ / Order", "Summons / Complaint", "Landlord-tenant", "Warrant paperwork", "Photos of service", "Body-cam / audio", "Other record"];

export default function ConstableDrop() {
  const [badge, setBadge] = useState("");
  const [who, setWho] = useState(null);
  const [calls, setCalls] = useState([]);
  const [services, setServices] = useState([]);
  const [svc, setSvc] = useState("");
  const [cc, setCc] = useState("");
  const [docType, setDocType] = useState(DOC_TYPES[0]);
  const [desc, setDesc] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const verify = async () => {
    setBusy(true);
    try {
      const r = await pub.post("/constable-drop/verify", { badge: badge.trim() });
      setWho(r.data.constable); setCalls(r.data.recent_calls || []); setServices(r.data.open_services || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Verification failed."); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    if (!files.length) { toast.error("Add at least one file."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("badge", badge.trim()); fd.append("cc_number", cc.trim()); fd.append("description", desc); fd.append("doc_type", docType); fd.append("service_number", svc.trim());
      files.forEach((f) => fd.append("files", f));
      const r = await pub.post("/constable-drop", fd);
      setDone(r.data); window.scrollTo(0, 0);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed."); }
    finally { setBusy(false); }
  };

  const reset = () => { setDone(null); setCc(""); setSvc(""); setDesc(""); setFiles([]); };
  const receipt = async () => {
    try {
      const r = await pub.get(`/constable-drop/${done.drop_no}/receipt.pdf`, { params: { badge: badge.trim() }, responseType: "blob" });
      const u = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = u; a.download = `ConstableDrop_${done.drop_no}_Receipt.pdf`; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(u), 5000);
    } catch { toast.error("Could not generate receipt."); }
  };
  const pickSvc = (v) => { setSvc(v); const s = services.find((x) => x.service_number === v); if (s?.linked_cc && !cc) setCc(s.linked_cc); };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="constable-drop">
      <div className="flex items-center gap-4">
        <div className="w-20 h-20 rounded-full shrink-0 flex items-center justify-center border-4" style={{ borderColor: P.gold, background: P.navy }}>
          <Gavel size={34} style={{ color: P.gold }} />
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: P.gold }}>Constable Services · Civil Process</div>
          <h2 className="text-2xl font-bold" style={{ color: P.navy }}>Constable Records Drop</h2>
          <p className="text-sm mt-1" style={{ color: P.dim }}>Upload civil-process paperwork, returns of service, photos and records so they attach directly to your call for service — no sign-in required. Identity is verified by your badge / unit ID.</p>
        </div>
      </div>

      {done ? (
        <div data-testid="constable-drop-receipt" className="border-2 rounded-sm p-6 space-y-3" style={{ borderColor: P.navy, background: "#fff" }}>
          <div className="flex items-center gap-2 font-bold text-lg" style={{ color: P.green }}><CheckCircle2 size={20} /> Upload received.</div>
          <div className="text-sm" style={{ color: P.ink }}>Drop Number: <b data-testid="constable-drop-number" className="font-mono text-lg">{done.drop_no}</b></div>
          <div data-testid="constable-drop-status" className="rounded-sm px-4 py-3 text-sm font-semibold"
            style={done.status === "attached" ? { background: "#E8F5EE", color: "#1E7B4F", border: "1px solid #1E7B4F" } : { background: "#FBF3E2", color: "#7A5A0E", border: `1px solid ${P.gold}` }}>
            {done.message}
          </div>
          {done.service_number && (
            <div data-testid="constable-drop-service" className="text-sm" style={{ color: P.ink }}>Service record: <b className="font-mono">{done.service_number}</b>{done.service_closed ? <span className="ml-2 px-2 py-0.5 text-xs font-bold rounded-sm" style={{ background: "#E8F5EE", color: "#1E7B4F" }}>SERVED — CLOSED</span> : null}</div>
          )}
          <ul className="text-xs space-y-1" style={{ color: P.dim }}>
            {done.files.map((f) => <li key={f.sha256} className="flex items-center gap-1.5"><FileText size={12} /> {f.filename} · {(f.size / 1024).toFixed(0)} KB · SHA-256 {f.sha256.slice(0, 12)}…</li>)}
          </ul>
          <div className="flex flex-wrap gap-2">
            <button data-testid="constable-drop-receipt-pdf" onClick={receipt} className="px-4 py-2 text-sm font-semibold rounded-sm text-white flex items-center gap-1.5" style={{ background: P.gold }}><FileText size={14} /> Download sealed receipt (PDF)</button>
            <button data-testid="constable-drop-another" onClick={reset} className="px-4 py-2 text-sm font-semibold rounded-sm text-white" style={{ background: P.navy }}>Upload more for another call</button>
          </div>
        </div>
      ) : !who ? (
        <div className="border rounded-sm p-6 space-y-3" style={{ borderColor: P.line, background: "#fff" }}>
          <label className="text-xs font-bold uppercase tracking-wider" style={{ color: P.navy }}>Badge / Unit ID</label>
          <div className="flex gap-2">
            <input data-testid="constable-badge" className={field} value={badge} onChange={(e) => setBadge(e.target.value)} placeholder="e.g. Westmoreland 61 or your username"
              onKeyDown={(e) => e.key === "Enter" && verify()} />
            <button data-testid="constable-verify" onClick={verify} disabled={busy || !badge.trim()}
              className="px-4 py-2 text-sm font-semibold rounded-sm text-white flex items-center gap-1.5 disabled:opacity-50" style={{ background: P.navy }}>
              {busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} Verify
            </button>
          </div>
          <p className="text-xs" style={{ color: P.dim }}>Must match an active constable / field-unit account provisioned by your agency administrator. All uploads are logged to the call's event history with your name and badge.</p>
        </div>
      ) : (
        <div className="border rounded-sm p-6 space-y-4" style={{ borderColor: P.line, background: "#fff" }}>
          <div data-testid="constable-verified" className="flex items-center gap-2 text-sm rounded-sm px-3 py-2" style={{ background: "#E8F5EE", color: "#1E7B4F" }}>
            <ShieldCheck size={15} /> Verified: <b>{who.name}</b> · {who.badge}{who.agency ? ` · ${who.agency}` : ""}
            <button data-testid="constable-change" onClick={() => { setWho(null); setCalls([]); }} className="ml-auto text-xs underline">change</button>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider" style={{ color: P.navy }}>Call for Service (CC#)</label>
            {calls.length > 0 && (
              <select data-testid="constable-recent-calls" className={`${field} mb-2`} value="" onChange={(e) => e.target.value && setCc(e.target.value)}>
                <option value="">— Pick from your recent calls —</option>
                {calls.map((c) => <option key={c.cc_number} value={c.cc_number}>{c.cc_number} · {c.call_type} · {c.address || "—"} · {c.status}</option>)}
              </select>
            )}
            <input data-testid="constable-cc" className={field} value={cc} onChange={(e) => setCc(e.target.value)} placeholder="2026-12345 (leave blank to route to Constable Intake)" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider" style={{ color: P.navy }}>Service of Process record (optional)</label>
            {services.length > 0 && (
              <select data-testid="constable-open-services" className={`${field} mb-2`} value={svc} onChange={(e) => pickSvc(e.target.value)}>
                <option value="">— Pick one of your open service records —</option>
                {services.map((s) => <option key={s.service_number} value={s.service_number}>{s.service_number} · {s.doc_type} · {s.servee_name} · {s.status}{s.linked_cc ? ` · CC ${s.linked_cc}` : ""}</option>)}
              </select>
            )}
            <input data-testid="constable-svc" className={field} value={svc} onChange={(e) => setSvc(e.target.value)} placeholder="SVC-2026-000123" />
            <p className="text-[11px] mt-1" style={{ color: P.dim }}>Filing a <b>Return of service</b> against a service record marks it SERVED and closes it automatically.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider" style={{ color: P.navy }}>Document type</label>
              <select data-testid="constable-doc-type" className={field} value={docType} onChange={(e) => setDocType(e.target.value)}>
                {DOC_TYPES.map((d) => <option key={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider" style={{ color: P.navy }}>Notes (optional)</label>
              <input data-testid="constable-desc" className={field} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Served 06/14 at 1430 hrs…" />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider" style={{ color: P.navy }}>Files (PDF, Word, images, audio · up to 25 per upload)</label>
            <label data-testid="constable-file-label" className="mt-1 flex items-center justify-center gap-2 border-2 border-dashed rounded-sm py-6 cursor-pointer text-sm" style={{ borderColor: P.gold, color: P.navy }}>
              <Upload size={16} /> Choose files
              <input data-testid="constable-files" type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => setFiles((p) => [...p, ...Array.from(e.target.files || [])].slice(0, 25))} />
            </label>
            {files.length > 0 && (
              <ul data-testid="constable-file-list" className="mt-2 space-y-1 text-xs" style={{ color: P.ink }}>
                {files.map((f, i) => (
                  <li key={i} className="flex items-center gap-2 border rounded-sm px-2 py-1" style={{ borderColor: P.line }}>
                    <FileText size={12} /> <span className="flex-1 truncate">{f.name}</span> <span style={{ color: P.dim }}>{(f.size / 1024).toFixed(0)} KB</span>
                    <button onClick={() => setFiles((p) => p.filter((_, j) => j !== i))} aria-label="remove"><X size={12} /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button data-testid="constable-submit" onClick={submit} disabled={busy || !files.length}
            className="w-full py-3 text-sm font-bold rounded-sm text-white flex items-center justify-center gap-2 disabled:opacity-50" style={{ background: P.navy }}>
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {cc.trim() ? `Attach to ${cc.trim().toUpperCase()}` : svc.trim() ? `File on ${svc.trim().toUpperCase()}` : "Send to Constable Intake"}
          </button>
        </div>
      )}
    </div>
  );
}
